import { store } from "../core/store";
import { audioManager } from "../core/audio";
import { serverNow } from "../core/clockSync";
import { GameState } from "../../shared/types";
import type { TimerDTO } from "../../shared/types";
import { buildLogoSvg } from "./logo";

const DRAWING_LOW_TIME_MS = 20_000;
const WORD_SELECTION_LOW_TIME_MS = 5_000;

/** Stress speeds up the DISPLAYED drawing countdown without changing when it
 * actually expires: the server shortens the real "drawing" timer's duration
 * (timer.durationMs) instead of the maximum time, so here we scale the raw
 * remaining time by (fullConfiguredMs / durationMs) to make the displayed number
 * start at the full configured duration and reach zero at the same real moment the
 * timer actually ends - "ticks down faster", not "starts from a smaller number".
 * For every other timer (and drawing turns with no Stress card active), durationMs
 * already equals the full configured duration, so this is a no-op (ratio of 1). */
function computeDisplayRemainingMs(timer: TimerDTO, rawRemainingMs: number): number {
  if (timer.label !== "drawing") return rawRemainingMs;
  const fullMs = (store.getState().config?.timers.drawingSeconds ?? 0) * 1000;
  if (fullMs <= 0 || timer.durationMs <= 0) return rawRemainingMs;
  return rawRemainingMs * (fullMs / timer.durationMs);
}

export function initLayout(): void {
  document.getElementById("game-title")!.appendChild(buildLogoSvg("logo logo--topbar"));

  const roundEl = document.getElementById("round-indicator")!;
  const wordEl = document.getElementById("word-display")!;
  const timerEl = document.getElementById("timer-display")!;
  const muteBtn = document.getElementById("mute-btn") as HTMLButtonElement;
  const muteIcon = muteBtn.querySelector("img") as HTMLImageElement;
  const volumeSlider = document.getElementById("volume-slider") as HTMLInputElement;

  function updateMuteIcon(): void {
    muteIcon.src = audioManager.muted ? "/images/icon_audio_muted.png" : "/images/icon_audio.png";
  }

  volumeSlider.value = String(audioManager.volume);
  updateMuteIcon();
  muteBtn.addEventListener("click", () => {
    audioManager.setMuted(!audioManager.muted);
    updateMuteIcon();
  });
  volumeSlider.addEventListener("input", () => {
    audioManager.setVolume(Number(volumeSlider.value));
  });

  store.subscribe((state) => {
    const snapshot = state.snapshot;
    if (snapshot && snapshot.totalRounds > 0) {
      const turnPart =
        snapshot.totalTurnsInRound > 0 ? ` — Turn ${snapshot.turnInRound}/${snapshot.totalTurnsInRound}` : "";
      roundEl.textContent = `Round ${snapshot.round}/${snapshot.totalRounds}${turnPart}`;
    } else {
      roundEl.textContent = "";
    }
    wordEl.textContent = state.wordView
      ? state.wordView.hidden
        ? "(?)"
        : `${state.wordView.display}  (${state.wordView.length})`
      : "";
  });

  let lastTimerKey: string | null = null;
  let playedLowWarning = false;

  setInterval(() => {
    const snapshot = store.getState().snapshot;
    const timer = snapshot?.timer;
    if (!timer) {
      if (timerEl.textContent !== "") timerEl.textContent = "";
      timerEl.classList.remove("timer-low");
      lastTimerKey = null;
      return;
    }

    const rawRemainingMs = timer.paused ? timer.remainingMsAtPause ?? 0 : Math.max(0, timer.endsAt - serverNow());
    const remainingMs = computeDisplayRemainingMs(timer, rawRemainingMs);
    const seconds = Math.ceil(remainingMs / 1000);
    // Ticks 4x a second but the text only changes once a second - skipping the
    // no-op writes avoids a DOM mutation (and style/layout work) per tick.
    const timerText = timer.paused ? `⏸ ${seconds}s` : `⏱ ${seconds}s`;
    if (timerEl.textContent !== timerText) timerEl.textContent = timerText;

    const timerKey = `${timer.label}:${timer.endsAt}`;
    if (timerKey !== lastTimerKey) {
      lastTimerKey = timerKey;
      playedLowWarning = false;
    }

    const isDrawingLow = snapshot?.state === GameState.DRAWING && timer.label === "drawing" && remainingMs < DRAWING_LOW_TIME_MS;
    const isWordSelectionLow =
      snapshot?.state === GameState.WORD_SELECTION &&
      timer.label === "wordSelection" &&
      remainingMs < WORD_SELECTION_LOW_TIME_MS;
    const isLow = !timer.paused && (isDrawingLow || isWordSelectionLow);

    timerEl.classList.toggle("timer-low", isLow);

    if (isLow && !playedLowWarning) {
      playedLowWarning = true;
      audioManager.play("countdownLow");
    }
  }, 250);
}
